import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Car, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MileageRatesCard = ({ rates, onSaved }) => {
  const [rows, setRows] = useState(rates || []);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(rows) !== JSON.stringify(rates);
  const upd = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/expenses`, { mileage_rates: rows.map((r) => ({ effective_from: r.effective_from, rate: Number(r.rate) })) });
      setRows(data.mileage_rates); onSaved?.(data);
      toast.success(`Mileage rates saved · ${data.trips_recalculated ?? 0} trip(s) recalculated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="mileage-rates-card">
      <CardHeader><CardTitle className="flex items-center gap-2"><Car className="w-5 h-5 text-blue-600" /> IRS standard mileage rates</CardTitle>
        <p className="text-sm text-slate-600">Each trip uses the latest rate whose effective date is on or before the trip date. 2026: $0.725 (Jan–Jun), $0.76 (from Jul 1). Saving recalculates every logged trip.</p></CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wide text-slate-500"><span className="col-span-5">Effective from</span><span className="col-span-4">Rate ($/mile)</span><span className="col-span-3" /></div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="mileage-rate-row">
            <Input className="col-span-5 h-8" type="date" value={r.effective_from} onChange={(e) => upd(i, { effective_from: e.target.value })} data-testid="mileage-rate-date" />
            <Input className="col-span-4 h-8" type="number" step="0.001" min="0" value={r.rate} onChange={(e) => upd(i, { rate: e.target.value })} data-testid="mileage-rate-value" />
            <div className="col-span-3 flex justify-end"><Button size="sm" variant="ghost" className="text-red-600" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid="mileage-rate-remove"><Trash2 className="w-3.5 h-3.5" /></Button></div>
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => setRows([...rows, { effective_from: `${new Date().getFullYear() + 1}-01-01`, rate: rows[rows.length - 1]?.rate || 0.7 }])} data-testid="mileage-rate-add"><Plus className="w-4 h-4 mr-1" /> Add rate</Button>
          <Button size="sm" disabled={!dirty || saving} onClick={save} data-testid="mileage-rates-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save rates"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
